import { useCallback, useEffect, useRef, useState } from "react";
import { Nullable, TriggeredHook } from "sonobello.utilities.react";

import { Customer } from "../../../../dtos/Customer";
import { LeadResult, LeadResultType } from "../../../../dtos/LeadResult";
import { useObx } from "../../../../utils/UseObx";
import ApiRequestPaths from "../../../Constants/ApiRequestPaths";
import INoteContentBuilder from "../../../Notes/Types/INoteContentBuilder";
import OpsReservation from "../../../Types/OpsReservation";
import { IConfirmedAppointmentResponse } from "../Components/ConfirmationStepWrapper";
import OpsAppointmentWithDistance, { IOpsAppointmentWithDistanceRequest } from "../Types/OpsAppointmentWithDistance";
import { IUseCreateLeadResultProps } from "./UseCreateLeadResult";

export interface IUseConfirmAppointmentProps<TNoteProps> {
  /** The properties required to build the appointment note. */
  noteProps: TNoteProps;
  /** The note content builder used to create the appointment note. */
  noteContentBuilder: INoteContentBuilder<TNoteProps>;
  /** The properties of the customer who will attend the confirmed appointment. */
  customer: Pick<Customer, "id" | "opportunity" | "dateOfBirth" | "email" | "phoneNumber" | "zipCode">;
  /** The identifier of the session's lead. */
  leadId: string;
  /** The hook which creates the result for the session's lead. */
  useCreateLeadResult: TriggeredHook<LeadResult, LeadResult, boolean, IUseCreateLeadResultProps>;
}

/** The appointment confirmation request response. */
export interface IAppointmentResponse {
  /** The assigned identifier of the created appointment. */
  id: string;
  /** The start time of the confirmed appointment as a UTC ISO. */
  startTimeUtc: string;
  /** The end time of the confirmed appointment as a UTC ISO. */
  endTimeUtc: string;
  /** The id of the customer who will attend the confirmed appointment. */
  customerId: string;
}

/** The appointment confirmation request response. */
export interface IOpsAppointmentResponse extends IAppointmentResponse {
  /** Distance to Center in miles relative to the customer */
  distanceToCenterInMiles: Nullable<number>;
}

/** A hook which dispatches the appointment and lead result for creation when triggered and returns the response.
 * @typeParam TNoteProps - The properties required to build the appointment note.
 */
const useConfirmOpsAppointmentWithDistance = <TNoteProps>({
  customer,
  leadId,
  noteContentBuilder,
  noteProps,
  useCreateLeadResult
}: IUseConfirmAppointmentProps<TNoteProps>): ReturnType<
  TriggeredHook<IConfirmedAppointmentResponse, OpsReservation, boolean, IUseConfirmAppointmentProps<TNoteProps>>
> => {
  const [result, setResult] = useState<Nullable<IConfirmedAppointmentResponse>>(null);
  const notePropsRef = useRef(noteProps);

  const {
    res: appointmentResult,
    err: appointmentError,
    loading: appointmentLoading,
    setReq: setAppointmentReq
  } = useObx<IOpsAppointmentResponse, IOpsAppointmentWithDistanceRequest>("Post Appointment", { method: "post" });

  const {
    result: leadResult,
    isLoading: isLeadResultLoading,
    error: leadResultError,
    execute: triggerPostLeadResult
  } = useCreateLeadResult({ leadId });

  useEffect(() => {
    if (appointmentResult)
      triggerPostLeadResult(
        new LeadResult(
          appointmentResult.payload.customerId,
          customer.opportunity.id,
          LeadResultType.Booked,
          appointmentResult.payload.id
        )
      );
  }, [appointmentResult]);

  useEffect(() => {
    if (leadResult && appointmentResult)
      setResult({
        appointment: {
          id: appointmentResult.payload.id,
          startTimeUtc: appointmentResult.payload.startTimeUtc,
          endTimeUtc: appointmentResult.payload.endTimeUtc
        },
        leadResult
      });
  }, [leadResult, appointmentResult]);

  useEffect(() => {
    notePropsRef.current = noteProps;
  }, [noteProps]);

  const execute = useCallback((opsReservation: OpsReservation) => {
    setAppointmentReq(
      q =>
        q && {
          ...q,
          url: ApiRequestPaths.postOpsAppointmentUrlV1_1(
            leadId,
            customer.id,
            opsReservation.center.id,
            opsReservation.slot.id
          ),
          payload: new OpsAppointmentWithDistance(
            opsReservation.center.id,
            customer.id,
            leadId,
            opsReservation.service,
            opsReservation.slot.startTimeUtc,
            opsReservation.slot.endTimeUtc,
            noteContentBuilder.buildNoteContent(notePropsRef.current),
            opsReservation.slot.id,
            opsReservation.center.distance
          )
        }
    );
  }, []);

  return {
    result,
    execute,
    isLoading: isLeadResultLoading || appointmentLoading,
    error: Boolean(leadResultError || appointmentError)
  };
};

export default useConfirmOpsAppointmentWithDistance;
